import React, { useState, useEffect } from 'react'
import { Typography, Box, Alert, Button } from '@mui/material'
import { SidebarLayout } from '@/components/Layout'
import { ProtectedRoute } from '@/components/common'
import { ListingFormTabs } from '@/components/Listings'
import { LoadingSpinner } from '@/components/common'
import { Listing, ListingForm as ListingFormType } from '@/types'
import { ListingsService } from '@/utils/listings-service'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowBack } from '@mui/icons-material'

export default function ListingDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  
  const [listing, setListing] = useState<Listing | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadListing = async () => {
      if (!id || id.trim() === '') {
        setError('No listing ID provided')
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        setError(null)
        console.log('Loading listing with ID:', id)
        const data = await ListingsService.getListing(id)
        console.log('Loaded listing data:', data)
        setListing(data)
      } catch (err) {
        console.error('Error loading listing:', err)
        setError(err instanceof Error ? err.message : 'Failed to load listing')
      } finally {
        setIsLoading(false)
      }
    }

    loadListing()
  }, [id])

  const displayError = error

  const handleSave = async (listingData: ListingFormType) => {
    if (!listing || !id) return
    
    try {
      setIsSaving(true)
      setError(null)
      
      const updatedListing = await ListingsService.updateListing(listing.listingId, listingData)
      setListing(updatedListing)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update listing')
    } finally {
      setIsSaving(false)
    }
  }

  const handlePublish = async (listingId: string) => {
    try {
      setIsSaving(true)
      setError(null)
      
      const updatedListing = await ListingsService.updateListingStatus(listingId, 'published')
      setListing(updatedListing)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to publish listing')
    } finally {
      setIsSaving(false)
    }
  }

  const handlePause = async (listingId: string) => {
    try {
      setIsSaving(true)
      setError(null)
      
      const newStatus = listing?.status === 'published' ? 'paused' : 'published'
      const updatedListing = await ListingsService.updateListingStatus(listingId, newStatus)
      setListing(updatedListing)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update listing status')
    } finally {
      setIsSaving(false)
    }
  }

  const handleActivate = async (listingId: string) => {
    try {
      setIsSaving(true)
      setError(null)
      
      const updatedListing = await ListingsService.activateListing(listingId)
      setListing(updatedListing)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to activate listing')
    } finally {
      setIsSaving(false)
    }
  }

  // Handle case where no ID is provided
  if (!id) {
    return (
      <SidebarLayout>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            No listing ID provided
          </Alert>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            component={Link}
            to="/listings"
          >
            Back to Listings
          </Button>
        </Box>
      </SidebarLayout>
    )
  }

  if (isLoading) {
    return (
      <SidebarLayout>
        <LoadingSpinner />
      </SidebarLayout>
    )
  }

  if (displayError && !listing) {
    return (
      <SidebarLayout>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {displayError}
          </Alert>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            component={Link}
            to="/listings"
          >
            Back to Listings
          </Button>
        </Box>
      </SidebarLayout>
    )
  }

  return (
    <SidebarLayout>
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            component={Link}
            to="/listings"
            sx={{ mb: 2 }}
          >
            Back to Listings
          </Button>
          
          <Typography variant="h3" component="h1" gutterBottom>
            Edit Listing
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Update your property details
          </Typography>
        </Box>

        {displayError && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {displayError}
          </Alert>
        )}

        {listing && (
          <>
            <ListingFormTabs
              key={listing.listingId} // Force re-render when listing changes
              listing={listing}
              onSave={handleSave}
              onPublish={handlePublish}
              onPause={handlePause}
              isLoading={isSaving}
            />
          </>
        )}
    </SidebarLayout>
  )
}